import { QuestionItem } from "../types/form";
import { visibleQuestionIds } from "./formVisibility";
import { questionInputName } from "./categoryValidation";
import { getValidationRules } from "./validationRules";

function toEntryObjects(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) {
    return value.map((entry) =>
      entry && typeof entry === "object" && !Array.isArray(entry)
        ? { ...(entry as Record<string, unknown>) }
        : {},
    );
  }
  if (value && typeof value === "object") {
    return [{ ...(value as Record<string, unknown>) }];
  }
  return [];
}

function readCount(formData: Record<string, unknown>, questionId: number): number | null {
  const raw =
    formData[`q_${questionId}`] ??
    formData[questionId] ??
    formData[String(questionId)];
  if (raw === undefined || raw === null || raw === "") return null;
  const count = Number(raw);
  if (!Number.isInteger(count) || count < 0) return null;
  return count;
}

/**
 * Grow-only: pad multi-entry composite answers so the array length is at least
 * the count reported by `initializeEntryCountFrom.questionId`. Never deletes
 * filled rows when the count shrinks.
 */
export function applyInitializeEntryCounts(
  formData: Record<string, any>,
  questions: QuestionItem[],
): boolean {
  let changed = false;
  // Transitive (client worklist #84): never pad a list whose count question
  // is itself hidden.
  const visible = visibleQuestionIds(questions, formData);

  for (const question of questions) {
    const rules = getValidationRules(question);
    const sourceId = Number(rules?.initializeEntryCountFrom?.questionId);
    if (!Number.isInteger(sourceId) || sourceId <= 0) continue;
    if ((question.answerType ?? question.type) !== "composite") continue;
    if (!question.allowMultipleAnswers) continue;
    if (!visible.has(Number(question.id))) continue;

    const count = readCount(formData, sourceId);
    if (count == null || count <= 0) continue;

    const inputName = questionInputName(question);
    const current = toEntryObjects(formData[inputName]);
    if (current.length >= count && Array.isArray(formData[inputName])) continue;

    const next = current.slice();
    while (next.length < count) next.push({});
    formData[inputName] = next;
    changed = true;
  }

  return changed;
}

function readRawAnswer(
  formData: Record<string, unknown>,
  questionId: number,
): unknown {
  return (
    formData[`q_${questionId}`] ??
    formData[questionId] ??
    formData[String(questionId)] ??
    ""
  );
}

/**
 * Stable dependency key for the initialize-entry-count effect. Includes the
 * source count answers and any visibility-trigger answers so padding re-runs
 * when a target question becomes visible.
 */
export function buildInitializeEntryCountEffectKey(
  formData: Record<string, unknown>,
  questions: QuestionItem[],
): string {
  const parts: string[] = [];
  for (const question of questions) {
    const sourceId = Number(
      getValidationRules(question)?.initializeEntryCountFrom?.questionId,
    );
    if (!Number.isInteger(sourceId) || sourceId <= 0) continue;

    parts.push(`q:${question.id}`);
    parts.push(`src:${sourceId}:${String(readRawAnswer(formData, sourceId))}`);
    for (const rule of question.visibilityRules ?? []) {
      const triggerId = Number(rule.triggerQuestionId);
      if (!Number.isInteger(triggerId) || triggerId <= 0) continue;
      parts.push(
        `vis:${triggerId}:${String(readRawAnswer(formData, triggerId))}`,
      );
    }
  }
  return parts.join("|");
}
